import { useMemo } from 'react';
import { Card } from '../../../layout/Card.jsx';
import { Icon } from '../../../layout/Icon.jsx';
import { formatIQD } from '../../../shared/format.js';
import { useAuth } from '../../../shared/useAuth.js';
import { useDiscounts } from '../../pricing/hooks/useDiscounts.js';
import { useCheckout } from '../hooks/useCheckout.js';

export function OverrideBox({ totals }) {
  const hasPermission = useAuth((s) => s.hasPermission);
  const override = useCheckout((s) => s.override);
  const setOverride = useCheckout((s) => s.setOverride);
  const toggleOverride = useCheckout((s) => s.toggleOverride);

  const allowed = hasPermission('checkout.override') || hasPermission('pos.discount');
  if (!allowed || !override.active) return null;

  // الخصم يُطبَّق على رسوم اللعب والدخول فقط — لا على فواتير نقطة البيع.
  // نقيّد الإدخال بنفس السقف الذي يفرضه الخادم حتى لا يفاجأ الكاشير برفض،
  // وهو نفسه أساس حساب الخصم الجاهز (النسبي) حتى لا تتجاوز النسبة السقف.
  const discountMax = Math.max(0, Number(totals?.discountMax || 0));
  const posDue = Math.max(0, Number(totals?.posDue || 0));
  const base = discountMax;
  // CheckoutScreen's totals memo already subtracts the active override from
  // totals.due, so just display it — don't subtract a second time.
  const newDue = Math.max(0, Number(totals?.due || 0));
  const overMax = Number(override.amount || 0) > discountMax;

  return (
    <Card>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <span className="grid place-items-center w-7 h-7 rounded-full bg-orange-soft text-orange-deep">
            <Icon name="edit" size={14} />
          </span>
          <div>
            <div className="font-bold text-[13px]">خصم</div>
            <div className="text-[11px] text-ink-3">على رسوم اللعب والدخول فقط • يُسجَّل مع سبب للتدقيق</div>
          </div>
        </div>
        <button
          type="button"
          className="btn btn-ghost"
          style={{ padding: 4 }}
          onClick={() => toggleOverride(false)}
        >
          <Icon name="x" size={14} />
        </button>
      </div>

      <PresetPicker base={base} value={override.presetId} onPick={setOverride} />

      <div className="grid gap-2" style={{ gridTemplateColumns: '160px 1fr' }}>
        <div>
          <label className="block text-[11px] font-semibold text-ink-2 mb-1">
            قيمة الخصم <span className="text-ink-3 font-normal">(حد أقصى {formatIQD(discountMax)})</span>
          </label>
          <input
            className="input"
            type="number"
            min="0"
            max={discountMax}
            value={override.amount || ''}
            onChange={(e) => {
              const v = Math.max(0, Math.min(discountMax, Number(e.target.value) || 0));
              // تعديل القيمة يدوياً يفصلها عن الخصم الجاهز المختار.
              setOverride({ amount: v, presetId: null });
            }}
            placeholder="0"
            disabled={discountMax <= 0}
          />
        </div>
        <div>
          <label className="block text-[11px] font-semibold text-ink-2 mb-1">سبب الخصم</label>
          <input
            className="input"
            value={override.reason || ''}
            onChange={(e) => setOverride({ reason: e.target.value })}
            placeholder="مثال: عميل دائم..."
          />
        </div>
      </div>

      {discountMax <= 0 ? (
        <div className="mt-2 text-[11.5px] text-red">
          لا توجد رسوم لعب مستحقة قابلة للخصم — فواتير نقطة البيع لا يُطبَّق عليها الخصم.
        </div>
      ) : null}
      {overMax ? (
        <div className="mt-2 text-[11.5px] text-red">
          تم تقييد الخصم إلى {formatIQD(discountMax)} (رسوم اللعب المستحقة).
        </div>
      ) : null}
      {posDue > 0 ? (
        <div className="mt-2 text-[11px] text-ink-3">
          {formatIQD(posDue)} من فواتير نقطة البيع تُحصَّل كاملة بدون خصم.
        </div>
      ) : null}

      <div className="mt-2 flex items-center justify-between text-[12px] px-2 py-1.5 rounded-[10px] bg-paper-2">
        <span className="text-ink-3">المتبقّي بعد الخصم</span>
        <b className="text-orange-deep">{formatIQD(newDue)}</b>
      </div>
    </Card>
  );
}

// قائمة منسدلة لاختيار خصم جاهز معرّف في إعدادات التسعير. عند الاختيار نحسب قيمة
// الخصم (نسبة من الأساس أو مبلغ مقطوع) ونغذّيها في مسار الخصم الموجود. نستثني خصم
// الأشقاء لأنه يُطبّق تلقائياً ضمن حساب رسوم اللعب عند التسوية، فاختياره هنا يكرّره.
function PresetPicker({ base, value, onPick }) {
  const { data } = useDiscounts();
  const presets = useMemo(() => {
    const list = Array.isArray(data) ? data : (data?.items || []);
    return list.filter((d) => d && d.isActive !== false && d.scope !== 'sibling');
  }, [data]);

  if (!presets.length) return null;

  function amountFor(d) {
    if (d.type === 'percent') {
      const pct = Math.min(100, Math.max(0, Number(d.value) || 0));
      return Math.min(base, Math.round((base * pct) / 100));
    }
    return Math.min(base, Math.max(0, Math.round(Number(d.value) || 0)));
  }

  function handleChange(id) {
    if (!id) {
      onPick({ presetId: null, amount: 0, reason: '' });
      return;
    }
    const d = presets.find((p) => String(p._id || p.id) === String(id));
    if (!d) return;
    onPick({ presetId: String(d._id || d.id), amount: amountFor(d), reason: d.name || '' });
  }

  return (
    <div className="mb-2">
      <label className="block text-[11px] font-semibold text-ink-2 mb-1">خصم جاهز</label>
      <select
        className="input"
        value={value || ''}
        onChange={(e) => handleChange(e.target.value)}
      >
        <option value="">— بدون خصم جاهز —</option>
        {presets.map((d) => {
          const id = String(d._id || d.id);
          const val = d.type === 'percent' ? `${Number(d.value || 0)}%` : formatIQD(d.value || 0);
          return (
            <option key={id} value={id}>
              {d.name} — {val}
            </option>
          );
        })}
      </select>
    </div>
  );
}
